// /teams/:teamId/projects/:projectId/settings — Project Settings page.
//
// Two panels:
//   1. Environments — editable per-env URL map (dev/staging/prod/…).
//      Writes to teams/{teamId}/projects/{projectId}.envs.
//   2. Secrets — list of secret names + last-rotated date; create / rotate
//      / delete via the /projectSecrets Function. Values never returned
//      to the UI per PRD §"Secrets entry".
//
// Client-side encryption with Web Crypto before transit is on the V1.5
// roadmap; V1 ships TLS-only with the secret value in the request body.

import { doc, updateDoc } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import { useProject } from '../../lib/data-hooks.js'
import { auth, db } from '../../lib/firebase.js'

const FUNCTIONS_URL =
  import.meta.env.VITE_FUNCTIONS_URL ?? 'https://us-central1-cairn-ebf35.cloudfunctions.net'

const NAME_RE = /^[A-Z][A-Z0-9_]*$/

async function callSecrets(action, body, getToken) {
  const token = await getToken()
  const res = await fetch(`${FUNCTIONS_URL}/projectSecrets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action, ...body }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '<no body>')
    throw new Error(`projectSecrets ${res.status}: ${text.slice(0, 200)}`)
  }
  return res.json()
}

function EnvsPanel({ teamId, projectId, envs }) {
  const [draft, setDraft] = useState(() => envs ?? { dev: { url: '' } })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)

  useEffect(() => {
    if (envs) setDraft(envs)
  }, [envs])

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await updateDoc(doc(db, 'teams', teamId, 'projects', projectId), { envs: draft })
      setSavedAt(new Date())
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  function updateUrl(envName, url) {
    setDraft((d) => ({ ...d, [envName]: { ...(d[envName] ?? {}), url } }))
  }

  function addEnv() {
    const name = prompt('Environment name (lowercase, e.g. staging)')
    if (!name) return
    if (draft[name]) return
    setDraft((d) => ({ ...d, [name]: { url: '' } }))
  }

  function removeEnv(envName) {
    setDraft((d) => {
      const next = { ...d }
      delete next[envName]
      return next
    })
  }

  return (
    <section className="mb-10 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-6">
      <h2 className="text-base font-medium text-[var(--color-basalt)]">Environments</h2>
      <p className="mt-1 text-sm text-[var(--color-schist)]">
        Per-env URLs. Pass <code className="font-mono">--env name</code> to{' '}
        <code className="font-mono">cairn run</code> to target one.
      </p>
      <form onSubmit={handleSave} className="mt-4 space-y-3">
        {Object.entries(draft).map(([envName, body]) => (
          <div key={envName} className="flex items-center gap-3">
            <span className="w-24 shrink-0 font-mono text-sm text-[var(--color-slate)]">
              {envName}
            </span>
            <input
              type="url"
              value={body?.url ?? ''}
              onChange={(e) => updateUrl(envName, e.target.value)}
              placeholder={`https://${envName}.example.com`}
              className="flex-1 rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-1.5 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
            />
            {envName !== 'dev' && (
              <Button type="button" variant="ghost" size="sm" onClick={() => removeEnv(envName)}>
                Remove
              </Button>
            )}
          </div>
        ))}
        <div className="flex items-center justify-between pt-3">
          <Button type="button" variant="ghost" size="sm" onClick={addEnv}>
            + Add environment
          </Button>
          <div className="flex items-center gap-3">
            {savedAt && (
              <span className="text-xs text-[var(--color-schist)]">
                saved {savedAt.toLocaleTimeString()}
              </span>
            )}
            {error && (
              <span className="text-xs text-[var(--color-failed)]" role="alert">
                {error}
              </span>
            )}
            <Button type="submit" variant="primary" size="sm" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </section>
  )
}

function SecretsPanel({ teamId, projectId, getToken }) {
  const [secrets, setSecrets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [value, setValue] = useState('')
  const [busyName, setBusyName] = useState(null)

  async function refresh() {
    setLoading(true)
    setError(null)
    try {
      const out = await callSecrets('list', { teamId, projectId }, getToken)
      setSecrets(out.secrets ?? [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch on team/project change only
  useEffect(() => {
    refresh()
  }, [teamId, projectId])

  async function handleCreate(e) {
    e.preventDefault()
    if (!NAME_RE.test(name)) {
      setError('Name must be UPPER_SNAKE_CASE (e.g. STRIPE_API_KEY)')
      return
    }
    setBusyName(name)
    setError(null)
    try {
      await callSecrets('createOrRotate', { teamId, projectId, name, value }, getToken)
      setName('')
      setValue('')
      setAdding(false)
      await refresh()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusyName(null)
    }
  }

  async function handleRotate(secretName) {
    const fresh = prompt(`Rotate "${secretName}" — enter the new value:`)
    if (!fresh) return
    setBusyName(secretName)
    setError(null)
    try {
      await callSecrets(
        'createOrRotate',
        { teamId, projectId, name: secretName, value: fresh },
        getToken,
      )
      await refresh()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusyName(null)
    }
  }

  async function handleDelete(secretName) {
    if (!confirm(`Delete "${secretName}"? This cannot be undone.`)) return
    setBusyName(secretName)
    setError(null)
    try {
      await callSecrets('delete', { teamId, projectId, name: secretName }, getToken)
      await refresh()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusyName(null)
    }
  }

  return (
    <section className="rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-6">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-base font-medium text-[var(--color-basalt)]">Secrets</h2>
          <p className="mt-1 text-sm text-[var(--color-schist)]">
            Cairn-managed via GCP Secret Manager. Values are never returned to the UI; rotate
            replaces the stored secret atomically.
          </p>
        </div>
        {!adding && (
          <Button type="button" variant="primary" size="sm" onClick={() => setAdding(true)}>
            Add secret
          </Button>
        )}
      </div>

      {adding && (
        <form
          onSubmit={handleCreate}
          className="mt-4 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/30 p-4"
        >
          <label className="block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              required
              placeholder="STRIPE_API_KEY"
              className="mt-1 w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-1.5 font-mono text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
            />
          </label>
          <label className="mt-3 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Value
            <input
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
              className="mt-1 w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-1.5 font-mono text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
            />
          </label>
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setAdding(false)
                setName('')
                setValue('')
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={Boolean(busyName) || !NAME_RE.test(name) || !value}
            >
              Save secret
            </Button>
          </div>
        </form>
      )}

      {error && (
        <p className="mt-4 text-sm text-[var(--color-failed)]" role="alert">
          {error}
        </p>
      )}

      <div className="mt-6">
        {loading && <p className="text-sm text-[var(--color-schist)]">Loading…</p>}
        {!loading && secrets.length === 0 && !adding && (
          <p className="text-sm text-[var(--color-schist)]">
            No secrets yet. Reference one from a flow with{' '}
            <code className="font-mono">{'{ secret: NAME }'}</code> and add it here.
          </p>
        )}
        {secrets.length > 0 && (
          <ul className="divide-y divide-[var(--color-sandstone)]">
            {secrets.map((s) => (
              <li key={s.name} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <div className="font-mono text-sm font-medium text-[var(--color-basalt)]">
                    {s.name}
                  </div>
                  {s.lastRotated && (
                    <div className="text-xs text-[var(--color-schist)]">
                      last rotated {new Date(s.lastRotated).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyName === s.name}
                    onClick={() => handleRotate(s.name)}
                  >
                    Rotate
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={busyName === s.name}
                    onClick={() => handleDelete(s.name)}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

export function ProjectSettings() {
  const { teamId, projectId } = useParams()
  const [user] = useAuthState(auth)
  const { data: project } = useProject(teamId, projectId)

  async function getToken() {
    if (!user) throw new Error('Not signed in')
    return await user.getIdToken()
  }

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: teamId, to: `/teams/${teamId}` },
          { label: project?.name ?? projectId, to: `/teams/${teamId}/projects/${projectId}` },
          { label: 'Settings' },
        ]}
        title="Project settings"
        subhead={project?.name ? `${project.name} · ${projectId}` : projectId}
      />
      <div className="mx-auto max-w-3xl px-6 py-8">
        <EnvsPanel teamId={teamId} projectId={projectId} envs={project?.envs} />
        <SecretsPanel teamId={teamId} projectId={projectId} getToken={getToken} />
      </div>
    </>
  )
}
